import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AppHeader, Button, Dialog, DropZone, IconButton, RichEditor, TextInput } from '@origo/origo-ui';
import { FloppyDiskIcon, XIcon } from '@phosphor-icons/react';
import type { KommunaltGalleriItem, KommunaltGalleriInput } from '../../services/kommunaltGalleriService';
import { sanitizeDescription } from '../renderHtml';
import styles from './KommunaltGalleriFormDialog.module.css';

interface Props {
    open: boolean;
    item?: KommunaltGalleriItem | null;
    saving?: boolean;
    error?: string | null;
    onClose: () => void;
    onSubmit: (input: KommunaltGalleriInput) => void;
}

export function KommunaltGalleriFormDialog({ open, item, saving, error, onClose, onSubmit }: Props) {
    const { t } = useTranslation();
    const dialogRef = useRef<HTMLDialogElement>(null);
    const [tittel, setTittel] = useState('');
    const [beskrivelse, setBeskrivelse] = useState('');
    const [bildefil, setBildefil] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);
    const [imageRemoved, setImageRemoved] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [contentErrorShown, setContentErrorShown] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [original, setOriginal] = useState<{ tittel: string; beskrivelse: string } | null>(null);

    const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];

    function hasText(html: string): boolean {
        return html.replace(/<[^>]*>/g, '').trim().length > 0;
    }

    useEffect(() => {
        if (open) {
            const initialTittel = item?.tittel ?? '';
            const initialBeskrivelse = item?.beskrivelse ?? '';
            setTittel(initialTittel);
            setBeskrivelse(initialBeskrivelse);
            setBildefil(null);
            setPreviewUrl(null);
            setFileError(null);
            setImageRemoved(false);
            setSubmitAttempted(false);
            setContentErrorShown(false);
            setShowConfirm(false);
            setOriginal({ tittel: initialTittel, beskrivelse: initialBeskrivelse });
        }
    }, [open, item]);

    // Sync dialog open/close — egen native <dialog> (som SectionEditorPage) i
    // stedet for origo-ui sin Dialog, slik at RichEditors lenkedialog ikke
    // stables oppå et lite, sentrert popup-kort.
    useEffect(() => {
        const el = dialogRef.current;
        if (!el) return;
        if (open) {
            if (!el.open) el.showModal();
        } else {
            if (el.open) el.close();
        }
    }, [open]);

    // Escape går via samme ulagret-endringer-sjekk som Avbryt-knappen
    useEffect(() => {
        const el = dialogRef.current;
        if (!el) return;
        function handleCancel(e: Event) {
            e.preventDefault();
            handleAvbryt();
        }
        el.addEventListener('cancel', handleCancel);
        return () => el.removeEventListener('cancel', handleCancel);
    });

    function handleFiles(files: File[]) {
        const file = files[0];
        if (!file) return;
        if (!ACCEPTED_TYPES.includes(file.type)) {
            setFileError(t('admin.kommunaltGalleriForm.invalidFileType'));
            return;
        }
        setFileError(null);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setBildefil(file);
        setPreviewUrl(URL.createObjectURL(file));
        setImageRemoved(false);
    }

    function handleRemoveImage() {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setBildefil(null);
        setPreviewUrl(null);
        setFileError(null);
        setImageRemoved(true);
    }

    function handleClose() {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        onClose();
    }

    function handleAvbryt() {
        if (hasUnsavedChanges) {
            setShowConfirm(true);
        } else {
            handleClose();
        }
    }

    function handleSubmit() {
        setSubmitAttempted(true);
        const trimmed = tittel.trim();
        setContentErrorShown(trimmed.length > 0 && !hasContent);
        if (!trimmed || !hasContent) return;
        onSubmit({
            tittel: trimmed,
            beskrivelse: beskrivelse.trim() || null,
            bildefil,
            removeBilde: imageRemoved,
        });
    }

    const isEdit = item != null;
    const displayUrl = previewUrl ?? (imageRemoved ? null : item?.bilde_url ?? null);
    const hasContent = hasText(beskrivelse) || displayUrl != null;
    const hasTitle = tittel.trim().length > 0;
    const hasUnsavedChanges = original !== null && (
        tittel !== original.tittel ||
        beskrivelse !== original.beskrivelse ||
        bildefil !== null ||
        imageRemoved
    );
    const canSubmit = !saving;

    return (
        <dialog ref={dialogRef} className={styles.page}>
            <AppHeader
                title={isEdit ? t('admin.kommunaltGalleriForm.titleEdit') : t('admin.kommunaltGalleriForm.titleNew')}
                onBack={handleAvbryt}
                backLabel={t('admin.kommunaltGalleriForm.cancel')}
                actions={
                    <Button
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        leftIcon={<FloppyDiskIcon size={16} />}
                    >
                        {saving ? t('admin.kommunaltGalleriForm.saving') : isEdit ? t('admin.kommunaltGalleriForm.save') : t('admin.kommunaltGalleriForm.add')}
                    </Button>
                }
            />
            <div className={styles.body}>
                <div className={styles.formPanel}>
                    {error && <p className={styles.error}>{error}</p>}
                    <TextInput
                        label={t('admin.kommunaltGalleriForm.titleLabel')}
                        value={tittel}
                        onChange={(e) => setTittel(e.target.value)}
                        error={submitAttempted && !hasTitle ? t('admin.kommunaltGalleriForm.titleRequired') : undefined}
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus
                    />

                    <RichEditor
                        label={t('admin.kommunaltGalleriForm.descriptionLabel')}
                        value={beskrivelse}
                        onChange={setBeskrivelse}
                        placeholder={t('admin.kommunaltGalleriForm.descriptionPlaceholder')}
                    />
                    {contentErrorShown && !hasContent && <p className={styles.error}>{t('admin.kommunaltGalleriForm.contentRequired')}</p>}
                    {displayUrl ? (
                        <div className={styles.imagePreviewWrapper}>
                            <p className={styles.imageLabel}>
                                {bildefil ? bildefil.name : t('admin.kommunaltGalleriForm.currentImage')}
                            </p>
                            <div className={styles.imagePreview}>
                                <img src={displayUrl} alt={tittel || t('admin.kommunaltGalleriForm.previewAlt')} />
                                <IconButton
                                    icon={<XIcon size={14} />}
                                    label={t('admin.kommunaltGalleriForm.removeImage')}
                                    variant="ghost"
                                    size="sm"
                                    className={styles.removeImageButton}
                                    onClick={handleRemoveImage}
                                />
                            </div>
                        </div>
                    ) : (
                        <>
                            <DropZone
                                label={t('admin.kommunaltGalleriForm.imageLabel')}
                                files={[]}
                                onFiles={handleFiles}
                                onRemove={handleRemoveImage}
                                accept="image/png,image/jpeg,image/svg+xml"
                                multiple={false}
                                title={t('admin.kommunaltGalleriForm.dropzoneTitle')}
                                hint={t('admin.kommunaltGalleriForm.dropzoneHint')}
                            />
                            {fileError && <p className={styles.error}>{fileError}</p>}
                        </>
                    )}
                </div>

                <div className={styles.previewPanel}>
                    <p className={styles.previewHeading}>{t('admin.kommunaltGalleriForm.previewHeading')}</p>
                    {hasTitle || displayUrl || hasText(beskrivelse) ? (
                        <figure className={styles.previewCard}>
                            {tittel && (
                                <div className={styles.previewTitleBar}>
                                    <strong className={styles.previewTitle}>{tittel}</strong>
                                </div>
                            )}
                            <figcaption className={styles.previewContent}>
                                {displayUrl && (
                                    <img src={displayUrl} alt={tittel || t('admin.kommunaltGalleriForm.previewAlt')} className={styles.previewImage} />
                                )}
                                {beskrivelse && (
                                    <div
                                        className={styles.previewDescription}
                                        dangerouslySetInnerHTML={{ __html: sanitizeDescription(beskrivelse) }}
                                    />
                                )}
                            </figcaption>
                        </figure>
                    ) : (
                        <div className={styles.previewEmpty}>
                            {t('admin.kommunaltGalleriForm.previewEmpty')}
                        </div>
                    )}
                </div>
            </div>

            <Dialog
                open={showConfirm}
                onClose={() => setShowConfirm(false)}
                title={t('admin.kommunaltGalleriForm.discardTitle')}
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowConfirm(false)}>
                            {t('admin.kommunaltGalleriForm.continueEditing')}
                        </Button>
                        <Button variant="danger" onClick={() => { setShowConfirm(false); handleClose(); }}>
                            {t('admin.kommunaltGalleriForm.discardChanges')}
                        </Button>
                    </>
                }
            >
                <p style={{ margin: 0 }}>
                    {t('admin.kommunaltGalleriForm.discardMessage')}
                </p>
            </Dialog>
        </dialog>
    );
}
